import { useState } from 'react';
import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

import { Weight } from '@/api';
import Box from '@/design_system/Box';
import InputSearch from '@/design_system/InputSearch';
import Stack from '@/design_system/Stack';
import { Address } from '@/models/address';
import { useShipmentArticles } from '@/models/article';
import { getEntityShippingAddress } from '@/models/entity';
import { Medium } from '@/models/medium';
import { ProductAttributeValue } from '@/models/productAttribute';
import { ArticleWithRelations, ShipmentWithRelations } from '@/models/shipment';
import { I18nWording } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';
import useDebouncedState from '@/utils/useDebouncedState';

import { newShipmentFormPlace, ShipmentFormPlace } from '../PlaceSelect/PlaceSelect';

import { AddedArticlesSection } from './components/AddedArticlesSection';
import { ShippableArticlesSection } from './components/ShippableArticlesSection';

import './ArticleSelect.css';

const { block, element } = createBEMClasses('article-select');

export type ShipmentFormArticle = {
  id: string;
  atClient: boolean;
  atEntityId: string | null;
  toClient: boolean;
  toEntityId: string | null;
  workshopId: string | null;
  fromEntityId: string | null;
  product: { name: string; data: { reference?: string } } | null;
  color: I18nWording | null;
  productAttributeValues: ProductAttributeValue[];
  weight: Weight | null;
  organization: {
    id: string;
    name: string;
  };
  request: {
    client:
      | (ShipmentFormPlace['client'] & {
          address: Address | null;
        })
      | null;
    store: { id: string; name: string } | null;
    organizationId: string;
    reference: string;
    workflowId: string;
  } | null;
  atEntity:
    | (ShipmentFormPlace['entity'] & {
        address: Address | null;
        logisticsAddress: Address | null;
      })
    | null;
  toEntity:
    | (ShipmentFormPlace['entity'] & {
        address: Address | null;
        logisticsAddress: Address | null;
      })
    | null;
  media?: Medium[] | undefined;
  quoteRefusedByTheClient: boolean;
  quoteRefusedDuringAnalysis: boolean;
  analysisRefusedAt: Date | null;
  scopes: ArticleWithRelations['scopes'];
};

export const getShipmentFormArticles = (shipment: ShipmentWithRelations) =>
  shipment.articles.map(({ article }) => ({
    ...article,
    request: {
      ...(article.request ?? article.archivedRequest)!,
      client: shipment.originClient,
    },
    atEntity: shipment.originEntity
      ? {
          ...shipment.originEntity,
          logisticsAddress: null,
        }
      : null,
    toEntity: shipment.destinationEntity
      ? {
          ...shipment.destinationEntity,
          logisticsAddress: null,
        }
      : null,
    organization: shipment.organization,
  })) satisfies ShipmentFormArticle[];

export const ArticleSelect = ({
  label,
  ariaLabel,
  addedArticles,
  setAddedArticles,
  origin,
  setOrigin,
  destination,
  setDestination,
}: {
  label: React.ReactNode;
  ariaLabel: string;
  addedArticles: ShipmentFormArticle[];
  setAddedArticles: (articles: ShipmentFormArticle[]) => void;
  origin?: ShipmentFormPlace;
  setOrigin: (address?: ShipmentFormPlace) => void;
  destination?: ShipmentFormPlace;
  setDestination: (address?: ShipmentFormPlace) => void;
}) => {
  const { t } = useLingui();

  const [query, debouncedQuery, setQuery] = useDebouncedState<string>('', 500);
  const [originalArticles] = useState<ShipmentFormArticle[]>(addedArticles);

  const [removedArticles, setRemovedArticles] = useState<ShipmentFormArticle[]>([]);

  const {
    data: { articles: fetchedArticles } = { articles: [] },
    isPending,
    isFetching,
  } = useShipmentArticles(
    {
      limit: 100,
      search: debouncedQuery,
      originClient: origin?.client?.id,
      originEntity: origin?.entity?.id,
      destinationClient: destination?.client?.id,
      destinationEntity: destination?.entity?.id,
    },
    {
      enabled: !!origin,
      keepPreviousData: true,
    }
  );

  const unaddedArticles = [...fetchedArticles, ...removedArticles]
    // Remove duplicates, article both in fetched and removed articles
    .filter((article, index, self) => self.findIndex((a) => a.id === article.id) === index)
    // Remove articles already added
    .filter((article) => !addedArticles.find((addedArticle) => addedArticle.id === article.id));

  const onAddArticle = (key: string) => {
    const article = unaddedArticles.find((article) => article.id === key);

    if (article) {
      setRemovedArticles(
        removedArticles.filter((removedArticle) => removedArticle.id !== article.id)
      );
      setAddedArticles([...addedArticles, article]);

      const clientAddress = article.request?.client?.address
        ? newShipmentFormPlace(article.request.client?.address, article.request.client, null)
        : undefined;

      const atEntityShippingAddress = getEntityShippingAddress(article.atEntity);
      const atEntityAddress = atEntityShippingAddress
        ? newShipmentFormPlace(atEntityShippingAddress, null, article.atEntity)
        : undefined;

      const toEntityShippingAddress = getEntityShippingAddress(article.toEntity);
      const toEntityAddress = toEntityShippingAddress
        ? newShipmentFormPlace(toEntityShippingAddress, null, article.toEntity)
        : undefined;

      if (!origin) {
        if (article.atClient) {
          setOrigin(clientAddress);
        } else if (article.atEntityId) {
          setOrigin(atEntityAddress);
        }
      }

      if (!destination) {
        if (article.toClient) {
          setDestination(clientAddress);
        } else if (article.toEntity) {
          setDestination(toEntityAddress);
        }
      }
    }
  };

  return (
    <Stack className={block()} gap="8px">
      {label}
      <InputSearch
        style={{ flex: 1 }}
        ariaLabel={ariaLabel}
        placeholder={t({
          id: 'shipments.new.article.placeholder',
          message: 'Search by item name or request reference...',
        })}
        value={query}
        onChange={setQuery}
        isLoading={isPending || isFetching}
      />

      <Box className={element('articles')} padding="0">
        <AddedArticlesSection
          addedArticles={addedArticles}
          unaddedArticles={unaddedArticles}
          setAddedArticles={setAddedArticles}
          setRemovedArticles={(article) => {
            if (originalArticles.find((a) => a.id === article.id)) {
              setRemovedArticles([...removedArticles, article]);
            }
          }}
        />

        <ShippableArticlesSection
          unaddedArticles={unaddedArticles}
          noItemsMessage={noItemsMessage({
            query,
            fetchedArticles,
            unaddedArticles,
            addedArticles,
          })}
          onAddArticle={onAddArticle}
          isFetching={isFetching}
        />
      </Box>
    </Stack>
  );
};

const noItemsMessage = ({
  query,
  fetchedArticles,
  unaddedArticles,
  addedArticles,
}: {
  query: string;
  fetchedArticles: ShipmentFormArticle[];
  unaddedArticles: ShipmentFormArticle[];
  addedArticles: ShipmentFormArticle[];
}): MessageDescriptor | null => {
  if (Boolean(query) && !fetchedArticles.length) {
    return msg({
      id: 'shipments.new.articles.shippable.search-no-results',
      message: 'No items match your search. Please try with different keywords.',
    });
  }

  if (!unaddedArticles.length) {
    if (addedArticles.length) {
      return msg({
        id: 'shipments.new.articles.shippable.empty.1',
        message: 'No other shippable items match this destination.',
      });
    } else {
      return msg({
        id: 'shipments.new.articles.shippable.empty.2',
        message: 'No shippable items match this destination.',
      });
    }
  }

  return null;
};
